// Attribution API service for PostgreSQL backend
const API_BASE_URL = process.env.REACT_APP_API_URL ||
                     (process.env.NODE_ENV === 'production'
                       ? 'https://api.onikanji.com'
                       : 'http://localhost:8080');

class AttributionApiService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/attribution`;
  }

  /**
   * Get authenticated headers with Firebase ID token
   * @param {string} idToken - Firebase ID token
   * @returns {Object} Headers object with authorization
   */
  getAuthHeaders(idToken) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    return headers;
  }

  /**
   * Track a new attribution visit
   * @param {Object} visitData - Visit data
   * @param {string} visitData.visitId - Unique visit identifier
   * @param {string} visitData.refId - Referrer ID (e.g., 'akane', 'brevo')
   * @param {string} visitData.utmSourceId - UTM source ID (e.g., 'youtubepodcast', 'email')
   * @param {string} visitData.landingPage - Landing page URL
   * @param {string} [visitData.campaign] - Campaign name
   * @returns {Promise<Object>} API response
   */
  async trackVisit(visitData) {
    try {
      const response = await fetch(`${this.baseUrl}/visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visitId: visitData.visitId,
          refId: visitData.refId,
          utmSourceId: visitData.utmSourceId,
          landingPage: visitData.landingPage,
          campaign: visitData.campaign,
          utmMedium: visitData.utmMedium,
          utmTerm: visitData.utmTerm,
          gclid: visitData.gclid,
          gbraid: visitData.gbraid,
          wbraid: visitData.wbraid
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to track visit');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - trackVisit error:', error);
      throw error;
    }
  }

  /**
   * Track a page view for existing attribution session
   * @param {string} visitId - Visit ID
   * @param {string} pagePath - Current page path
   * @returns {Promise<Object>} API response
   */
  async trackPageView(visitId, pagePath) {
    try {
      const response = await fetch(`${this.baseUrl}/pageview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visitId,
          pagePath
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to track page view');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - trackPageView error:', error);
      throw error;
    }
  }

  /**
   * Track a conversion (signup) for attribution session
   * @param {string} visitId - Visit ID
   * @param {string} userId - User ID who converted
   * @param {string} [displayName] - User's display name
   * @returns {Promise<Object>} API response
   */
  async trackConversion(visitId, userId, displayName) {
    try {
      const response = await fetch(`${this.baseUrl}/conversion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visitId,
          userId,
          displayName
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to track conversion');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - trackConversion error:', error);
      throw error;
    }
  }

  /**
   * Get attribution data with optional filters
   * @param {string} [timeRangeDays='30'] - Number of days to look back
   * @param {string|null} [referrerFilter=null] - Optional referrer to filter by
   * @param {string|null} [utmSourceFilter=null] - Optional UTM source to filter by
   * @param {string} idToken - Firebase ID token for authentication
   * @returns {Promise<Object>} API response with attribution data
   */
  async getAttributionData(timeRangeDays = '30', referrerFilter = null, utmSourceFilter = null, idToken) {
    try {
      const params = new URLSearchParams();
      params.append('timeRangeDays', timeRangeDays);
      
      if (referrerFilter) {
        params.append('referrerFilter', referrerFilter);
      }
      
      if (utmSourceFilter) {
        params.append('utmSourceFilter', utmSourceFilter);
      }

      const response = await fetch(`${this.baseUrl}/data?${params.toString()}`, {
        method: 'GET',
        headers: this.getAuthHeaders(idToken),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get attribution data');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - getAttributionData error:', error);
      throw error;
    }
  }

  /**
   * Get dynamic list of available referrers from the database
   * @param {string} idToken - Firebase ID token for authentication
   * @returns {Promise<Object>} API response with available referrers
   */
  async getReferrers(idToken) {
    try {
      const response = await fetch(`${this.baseUrl}/referrers`, {
        method: 'GET',
        headers: this.getAuthHeaders(idToken),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get referrers');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - getReferrers error:', error);
      throw error;
    }
  }

  /**
   * Get attribution conversion data with subscription information
   * @param {string} [timeRangeDays='30'] - Number of days to look back
   * @param {string|null} [referrerFilter=null] - Optional referrer to filter by
   * @param {string|null} [utmSourceFilter=null] - Optional UTM source to filter by
   * @param {string} idToken - Firebase ID token for authentication
   * @returns {Promise<Object>} API response with attribution conversion data including subscriptions
   */
  async getAttributionConversions(timeRangeDays = '30', referrerFilter = null, utmSourceFilter = null, idToken) {
    try {
      const params = new URLSearchParams();
      params.append('timeRangeDays', timeRangeDays);
      
      if (referrerFilter) {
        params.append('referrerFilter', referrerFilter);
      }
      
      if (utmSourceFilter) {
        params.append('utmSourceFilter', utmSourceFilter);
      }

      const response = await fetch(`${this.baseUrl}/conversions?${params.toString()}`, {
        method: 'GET',
        headers: this.getAuthHeaders(idToken),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get attribution conversion data');
      }

      return await response.json();
    } catch (error) {
      console.error('Attribution API - getAttributionConversions error:', error);
      throw error;
    }
  }
}

export const attributionApiService = new AttributionApiService();